import { useState, useEffect } from 'react'
import {
  Heart,
  MessageCircle,
  User,
  Trash2,
  Send,
  X,
  Tag,
  Gamepad2,
  Trophy,
  Calendar,
  FolderOpen,
  Pencil,
  Check
} from 'lucide-react'
import { screenshotsAPI, albumsAPI } from '@/lib/api'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Card, CardContent } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'

export default function ScreenshotDetailModal({
  screenshot,
  open,
  onOpenChange,
  currentUser,
  onUpdate,
  onDelete
}) {
  const [comment, setComment] = useState('')
  const [submittingComment, setSubmittingComment] = useState(false)
  const [deletingComment, setDeletingComment] = useState(null)
  const [liking, setLiking] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState(null)
  const [albums, setAlbums] = useState([])
  const [movingToAlbum, setMovingToAlbum] = useState(false)
  const [editing, setEditing] = useState(false)
  const [editTitle, setEditTitle] = useState('')
  const [editDescription, setEditDescription] = useState('')
  const [saving, setSaving] = useState(false)

  // Fetch albums for admin move functionality
  useEffect(() => {
    if (['admin','superadmin'].includes(currentUser?.role)) {
      const fetchAlbums = async () => {
        try {
          const response = await albumsAPI.getAlbums()
          setAlbums(response.data.albums || [])
        } catch (error) {
          console.error('Error fetching albums:', error)
          setAlbums([])
        }
      }
      fetchAlbums()
    }
  }, [currentUser])

  // Handle move to album (admin only)
  const handleMoveToAlbum = async (albumId) => {
    if (!['admin','superadmin'].includes(currentUser?.role)) return

    try {
      setMovingToAlbum(true)
      setError(null)

      const resolvedAlbumId = albumId === 'none' ? null : albumId
      await albumsAPI.moveScreenshotToAlbum(screenshot.id, resolvedAlbumId)

      onUpdate(
        { ...screenshot, album_id: resolvedAlbumId },
        screenshot.album_id
      )
    } catch (error) {
      console.error('Move to album error:', error)
      setError(error.response?.data?.error || 'Failed to move screenshot to album')
    } finally {
      setMovingToAlbum(false)
    }
  }

  // Handle like/unlike
  const handleToggleLike = async () => {
    if (!currentUser) return

    try {
      setLiking(true)
      setError(null)

      if (screenshot.user_liked) {
        await screenshotsAPI.unlikeScreenshot(screenshot.id)
        onUpdate({
          ...screenshot,
          user_liked: false,
          likes_count: screenshot.likes_count - 1
        })
      } else {
        await screenshotsAPI.likeScreenshot(screenshot.id)
        onUpdate({
          ...screenshot,
          user_liked: true,
          likes_count: screenshot.likes_count + 1
        })
      }
    } catch (error) {
      console.error('Like toggle error:', error)
      setError(error.response?.data?.error || 'Failed to update like')
    } finally {
      setLiking(false)
    }
  }

  // Handle add comment
  const handleAddComment = async (e) => {
    e.preventDefault()

    if (!comment.trim()) return

    try {
      setSubmittingComment(true)
      setError(null)

      const response = await screenshotsAPI.addComment(screenshot.id, comment.trim())

      // Add new comment to list
      onUpdate({
        ...screenshot,
        comments: [response.data, ...screenshot.comments],
        comments_count: screenshot.comments_count + 1
      })

      setComment('')
    } catch (error) {
      console.error('Add comment error:', error)
      setError(error.response?.data?.error || 'Failed to add comment')
    } finally {
      setSubmittingComment(false)
    }
  }

  // Handle delete comment
  const handleDeleteComment = async (commentId) => {
    try {
      setDeletingComment(commentId)
      setError(null)

      await screenshotsAPI.deleteComment(commentId)

      // Remove comment from list
      onUpdate({
        ...screenshot,
        comments: screenshot.comments.filter(c => c.id !== commentId),
        comments_count: screenshot.comments_count - 1
      })
    } catch (error) {
      console.error('Delete comment error:', error)
      setError(error.response?.data?.error || 'Failed to delete comment')
    } finally {
      setDeletingComment(null)
    }
  }

  // Handle delete screenshot
  const handleDeleteScreenshot = async () => {
    if (!confirm('Are you sure you want to delete this screenshot?')) return

    try {
      setDeleting(true)
      setError(null)

      await screenshotsAPI.deleteScreenshot(screenshot.id)

      // Call parent's onDelete
      onDelete(screenshot.id)
    } catch (error) {
      console.error('Delete screenshot error:', error)
      setError(error.response?.data?.error || 'Failed to delete screenshot')
    } finally {
      setDeleting(false)
    }
  }

  const canEdit = currentUser && (
    currentUser.id === screenshot.user_id || ['admin','superadmin'].includes(currentUser?.role)
  )
  const canDelete = canEdit

  const startEdit = () => {
    setEditTitle(screenshot.title)
    setEditDescription(screenshot.description || '')
    setEditing(true)
  }

  const handleSaveEdit = async () => {
    if (!editTitle.trim()) return
    try {
      setSaving(true)
      setError(null)
      const response = await screenshotsAPI.updateScreenshot(screenshot.id, {
        title: editTitle.trim(),
        description: editDescription.trim()
      })
      onUpdate({ ...screenshot, title: response.data.title, description: response.data.description })
      setEditing(false)
    } catch (err) {
      setError(err.response?.data?.error || 'Opslaan mislukt')
    } finally {
      setSaving(false)
    }
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              {editing ? (
                <div className="space-y-2">
                  <Input
                    value={editTitle}
                    onChange={e => setEditTitle(e.target.value)}
                    className="text-lg font-bold"
                    placeholder="Titel"
                    autoFocus
                  />
                  <Input
                    value={editDescription}
                    onChange={e => setEditDescription(e.target.value)}
                    className="text-sm"
                    placeholder="Beschrijving (optioneel)"
                  />
                </div>
              ) : (
                <>
                  <DialogTitle className="text-2xl">{screenshot.title}</DialogTitle>
                  {screenshot.description && (
                    <p className="text-muted-foreground mt-2">{screenshot.description}</p>
                  )}
                </>
              )}
            </div>
            <div className="flex gap-2 shrink-0">
              {canEdit && !editing && (
                <Button variant="outline" size="sm" onClick={startEdit}>
                  <Pencil className="h-4 w-4 mr-1" /> Bewerken
                </Button>
              )}
              {editing && (
                <>
                  <Button size="sm" onClick={handleSaveEdit} disabled={saving || !editTitle.trim()}>
                    <Check className="h-4 w-4 mr-1" /> {saving ? 'Opslaan...' : 'Opslaan'}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setEditing(false)} disabled={saving}>
                    <X className="h-4 w-4" />
                  </Button>
                </>
              )}
              {canDelete && !editing && (
                <Button variant="destructive" size="sm" onClick={handleDeleteScreenshot} disabled={deleting}>
                  <Trash2 className="h-4 w-4 mr-1" />
                  {deleting ? 'Verwijderen...' : 'Verwijderen'}
                </Button>
              )}
            </div>
          </div>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-4">
          {/* Left side - Image and details */}
          <div className="lg:col-span-2 space-y-4">
            {/* Screenshot Image */}
            <div className="bg-gray-900 rounded-lg overflow-hidden">
              <img
                src={screenshotsAPI.getScreenshotUrl(screenshot.file_path)}
                alt={screenshot.title}
                className="w-full h-auto"
              />
            </div>

            {/* Metadata */}
            <Card>
              <CardContent className="p-4">
                <div className="space-y-3">
                  {/* Uploader */}
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">
                      Uploaded by <strong>{screenshot.uploader_username}</strong>
                    </span>
                  </div>

                  {/* Date */}
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{formatDate(screenshot.created_at)}</span>
                  </div>

                  {/* Game */}
                  {screenshot.game && (
                    <div className="flex items-center gap-2">
                      <Gamepad2 className="h-4 w-4 text-muted-foreground" />
                      <Badge variant="secondary">{screenshot.game}</Badge>
                    </div>
                  )}

                  {/* Tournament */}
                  {screenshot.tournament_name && (
                    <div className="flex items-center gap-2">
                      <Trophy className="h-4 w-4 text-muted-foreground" />
                      <Badge variant="secondary">{screenshot.tournament_name}</Badge>
                    </div>
                  )}

                  {/* Tagged Users */}
                  {screenshot.tags && screenshot.tags.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Tag className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm font-medium">Tagged:</span>
                      </div>
                      <div className="flex flex-wrap gap-2 ml-6">
                        {screenshot.tags.map((tag) => (
                          <Badge key={tag.id} variant="outline">
                            <User className="h-3 w-3 mr-1" />
                            {tag.username}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Move to Album (Admin only) */}
                  {['admin','superadmin'].includes(currentUser?.role) && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <FolderOpen className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm font-medium">Album:</span>
                      </div>
                      <div className="ml-6">
                        <Select
                          value={screenshot.album_id?.toString() || 'none'}
                          onValueChange={(value) => handleMoveToAlbum(value)}
                          disabled={movingToAlbum}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="No album" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">No album</SelectItem>
                            {albums.map((album) => (
                              <SelectItem key={album.id} value={album.id.toString()}>
                                {album.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Like Button */}
            <div className="flex items-center gap-4">
              <Button
                variant={screenshot.user_liked ? "default" : "outline"}
                onClick={handleToggleLike}
                disabled={!currentUser || liking}
                className={screenshot.user_liked ? "bg-red-600 hover:bg-red-700" : ""}
              >
                <Heart className={`h-4 w-4 mr-2 ${screenshot.user_liked ? 'fill-current' : ''}`} />
                {screenshot.likes_count} {screenshot.likes_count === 1 ? 'Like' : 'Likes'}
              </Button>
              <span className="flex items-center gap-2 text-muted-foreground">
                <MessageCircle className="h-4 w-4" />
                {screenshot.comments_count} {screenshot.comments_count === 1 ? 'Comment' : 'Comments'}
              </span>
            </div>
          </div>

          {/* Right side - Comments */}
          <div className="space-y-4">
            <h3 className="font-semibold text-lg flex items-center gap-2">
              <MessageCircle className="h-5 w-5" />
              Comments
            </h3>

            {/* Add Comment Form */}
            {currentUser && (
              <form onSubmit={handleAddComment} className="flex gap-2">
                <Input
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Add a comment..."
                  disabled={submittingComment}
                />
                <Button
                  type="submit"
                  size="icon"
                  disabled={submittingComment || !comment.trim()}
                >
                  <Send className="h-4 w-4" />
                </Button>
              </form>
            )}

            {/* Error */}
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* Comments List */}
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {screenshot.comments && screenshot.comments.length > 0 ? (
                screenshot.comments.map((c) => (
                  <Card key={c.id}>
                    <CardContent className="p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <User className="h-3 w-3 text-muted-foreground" />
                            <span className="text-sm font-medium">{c.username}</span>
                            <span className="text-xs text-muted-foreground">
                              {formatDate(c.created_at)}
                            </span>
                          </div>
                          <p className="text-sm">{c.comment}</p>
                        </div>
                        {currentUser && (currentUser.id === c.user_id || ['admin','superadmin'].includes(currentUser?.role)) && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => handleDeleteComment(c.id)}
                            disabled={deletingComment === c.id}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))
              ) : (
                <p className="text-sm text-muted-foreground text-center py-8">
                  No comments yet. Be the first to comment!
                </p>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
